/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import { Euler, Vector2, Vector3 } from 'three';

// Sourced from package.json at build time via Vite's `define`.
// Edit package.json's `version` field to bump.
// The `typeof` guards keep this module loadable under plain `tsx` (the test
// scripts), where no Vite define exists: any test whose import chain reached
// here used to die with "__APP_VERSION__ is not defined" and had to shim the
// global by hand before its first import.
export const VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0-dev';

// Stamped at build time (ISO 8601 UTC) via Vite's `define`.
export const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : new Date().toISOString();

// BUILD_TIME formatted in America/New_York time, e.g. "2026-05-06 14:42 EDT".
export const BUILD_TIME_EST = (() => {
  const d = new Date(BUILD_TIME);
  const date = d.toLocaleString('sv-SE', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const tz = d.toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' }).split(' ').pop();
  return `${date} ${tz}`;
})();

export const HOME_URL: string =
  (typeof window !== 'undefined' ? window.location.origin : 'https://aims.intofuture.org') + '/';

export const MAXIMUM_NUMBER_OF_ATOMS_FOR_SIMULATION = 500;
// Past MAXIMUM_NUMBER_OF_ATOMS_FOR_SIMULATION the lab WARNS (it runs, more
// slowly) — the user may still load and run more. Only this hard ceiling
// refuses, so a mistyped count cannot freeze the tab.
export const HARD_MAXIMUM_NUMBER_OF_ATOMS_FOR_SIMULATION = 2000;

// Days a soft-deleted (trashed) project is retained before it is permanently
// removed. Deletion is done natively by a Firestore TTL policy on the `purgeAt`
// Timestamp field of the `projects` collection group (no Cloud Function) — at
// trash time deleteProject stamps purgeAt = now + this many days. The UI
// countdown uses the same constant. If you change it, the new value only
// affects newly-trashed projects (existing purgeAt stamps are already fixed).
export const TRASH_RETENTION_DAYS = 30;

// Crystal lab atom counts. Past MAXIMUM_NUMBER_OF_ATOMS_FOR_CRYSTAL the lab
// WARNS that the simulation may run slowly but still loads and runs it — the
// user decides. Only HARD_MAXIMUM_NUMBER_OF_ATOMS_FOR_CRYSTAL refuses, so a
// mistyped 20×20×20 tiling cannot freeze the tab (the bond pass is O(N²) and
// the viewer mounts one mesh per atom) — the same 500 / 2000 split as the
// polymer chain builder. Default tilings (a fresh load, one carried over from
// the previous crystal) are still sized to fit under the soft limit.
export const MAXIMUM_NUMBER_OF_ATOMS_FOR_CRYSTAL = 500;
export const HARD_MAXIMUM_NUMBER_OF_ATOMS_FOR_CRYSTAL = 2000;

export const DEFAULT_FOV = 30;
export const DEFAULT_SHADOW_CAMERA_FAR = 10000;
export const DEFAULT_SHADOW_MAP_SIZE = 4096;
export const DEFAULT_LIGHT_INTENSITY = 5;

export const UNDO_SHOW_INFO_DURATION = 0.5;

export const MOVE_HANDLE_COLOR = 'white';
export const HIGHLIGHT_HANDLE_COLOR = 'yellow';

export const GRID_RATIO = 5;
export const FINE_GRID_SCALE = 0.1;
export const NORMAL_GRID_SCALE = GRID_RATIO * FINE_GRID_SCALE;

export const Z_INDEX_FRONT_PANEL = 15;

export const HALF_PI = Math.PI / 2;

export const TWO_PI = Math.PI * 2;

export const ZERO_TOLERANCE = 0.0001;

export const UNIT_VECTOR_POS_Z_ARRAY = [0, 0, 1];

export const UNIT_VECTOR_NEG_Y_ARRAY = [0, -1, 0];

export const UNIT_VECTOR_POS_X = new Vector3(1, 0, 0);

export const UNIT_VECTOR_NEG_X = new Vector3(-1, 0, 0);

export const UNIT_VECTOR_POS_Y = new Vector3(0, 1, 0);

export const UNIT_VECTOR_NEG_Y = new Vector3(0, -1, 0);

export const UNIT_VECTOR_POS_Z = new Vector3(0, 0, 1);

export const UNIT_VECTOR_NEG_Z = new Vector3(0, 0, -1);

export const ORIGIN_VECTOR2 = new Vector2(0, 0);

export const ORIGIN_VECTOR3 = new Vector3(0, 0, 0);

export const HALF_PI_Z_EULER = new Euler(0, 0, HALF_PI);

export const REGEX_ALLOWABLE_IN_NAME = /^[A-Za-z0-9\s-_()!?%&,]*$/;

export const DEFAULT_CAMERA_POSITION = [50, 50, 50];

export const DEFAULT_CAMERA_ROTATION = [0, 0, 0];

export const DEFAULT_CAMERA_UP = [0, 0, 1];

export const DEFAULT_PAN_CENTER = [0, 0, 0];

export enum FirebaseName {
  FILES = 'Files',
  LOG_DATA = 'Log Data',
}

export enum MoveDirection {
  Left = 'Left',
  Right = 'Right',
  Up = 'Up',
  Down = 'Down',
}

export enum FlightControl {
  RollLeft = 'Roll Left',
  RollRight = 'Roll Right',
  PitchUp = 'Pitch Up',
  PitchDown = 'Pitch Down',
  YawLeft = 'Yaw Left',
  YawRight = 'Yaw Right',
  MoveForward = 'Move Forward',
  MoveBackward = 'Move Backward',
  RotateAroundXClockwise = 'Rotate around X Clockwise',
  RotateAroundXCounterclockwise = 'Rotate around X Counterclockwise',
  RotateAroundYClockwise = 'Rotate around Y Clockwise',
  RotateAroundYCounterclockwise = 'Rotate around Y Counterclockwise',
  RotateAroundZClockwise = 'Rotate around Z Clockwise',
  RotateAroundZCounterclockwise = 'Rotate around Z Counterclockwise',
  TranslateInPositiveX = 'Translate in Positive X',
  TranslateInNegativeX = 'Translate in Negative X',
  TranslateInPositiveY = 'Translate in Positive Y',
  TranslateInNegativeY = 'Translate in Negative Y',
  TranslateInPositiveZ = 'Translate in Positive Z',
  TranslateInNegativeZ = 'Translate in Negative Z',
}

export enum Language {
  English = 'English',
  ChineseSimplified = '简体中文',
  ChineseTraditional = '繁体中文',
}

export enum ProjectType {
  DRUG_DISCOVERY = 'Drug Discovery',
  MOLECULAR_MODELING = 'Molecular Modeling',
  CRYSTAL_BUILDER = 'Crystal Builder',
  SEMICONDUCTOR_DESIGN = 'Semiconductor Design',
  NANOMATERIAL_DESIGN = 'Nanomaterial Design',
  POLYMER_BUILDER = 'Polymer Builder',
  PROTEIN_LAB = 'Protein Lab',
  DNA_RNA_LAB = 'DNA/RNA Lab',
}

// The two "biomolecule" project types share one view-only rendering/state/loader
// core (projectState.biomolecule, bioStructureViewer); they differ only in
// defaults, the sequence-builder mode, examples, and (later) agent persona.
// The three "crystal core" project types share the crystal lab, gallery, chat and
// agent (lattices, Tersoff / EAM / Stillinger–Weber, periodic or padded box).
// Semiconductor Design adds band structure and doping on top; Nanomaterial
// Design adds finite nanostructures cut from the lattice. Branch on this
// helper wherever the UI routes between the molecule and crystal pipelines.
export const isCrystalCoreProject = (type: ProjectType | undefined | null): boolean =>
  type === ProjectType.CRYSTAL_BUILDER ||
  type === ProjectType.SEMICONDUCTOR_DESIGN ||
  type === ProjectType.NANOMATERIAL_DESIGN;

export const isBiomoleculeProject = (type: ProjectType): boolean =>
  type === ProjectType.PROTEIN_LAB || type === ProjectType.DNA_RNA_LAB;

export enum MoleculeType {
  ANY = 'Any',
  MONATOMIC = 'Monatomic',
  CRYSTAL = 'Crystal',
  COMMON = 'Common',
  HYDROCARBON = 'Hydrocarbon',
  BIOMOLECULE = 'Biomolecule',
  DRUG = 'Drug',
}

export enum LabelType {
  NAME = 'Name',
  FORMULA = 'Formula',
}

export enum GalleryViewMode {
  GRID = 'Grid',
  CHAT = 'Chat',
  COMPARE = 'Compare',
  LIST = 'List',
}

export enum GraphType {
  PARALLEL_COORDINATES = 'Parallel Coordinates',
  SCATTER_PLOT = 'Scatter Plot',
  PCA_PROJECTION = 'PCA Projection',
  HISTOGRAM = 'Histogram',
  CORRELATION_HEATMAP = 'Correlation Heatmap',
  RADAR_CHART = 'Radar Chart',
  BUBBLE_CHART = 'Bubble Chart',
  SIMILARITY_HEATMAP = 'Similarity Heatmap',
  SIMILARITY_PROJECTION = 'Similarity Projection',
  STRUCTURE_PROPERTY_SCATTER = 'Structure-Property Scatter',
  ACTIVITY_CLIFFS = 'Activity Cliffs',
  SCAFFOLD_CLUSTERS = 'Scaffold Clusters',
  BIT_CORRELATIONS = 'Bit Correlations',
  PARETO_FRONTIER = 'Pareto Frontier',
}

export enum BondType {
  SINGLE_BOND = 0,
  DOUBLE_BOND = 1,
  TRIPLE_BOND = 2,
  HYDROGEN_BOND = 3,
  VAN_DER_WAALS_BOND = 4,
}

export enum ObjectType {
  Atom = 'Atom',
  Bond = 'Bond',
  Molecule = 'Molecule',
  Protein = 'Protein',
  Ligand = 'Ligand',
  Spaceship = 'Spaceship',
}

export enum DataColoring {
  ALL = 0,
  INDIVIDUALS = 1,
  GROUPS = 2,
}

export enum SpaceshipDisplayMode {
  NONE = 0,
  OUTSIDE_VIEW = 1,
  INSIDE_VIEW = 2,
}

export enum PickMode {
  MOLECULE = 'molecule',
  ATOM = 'atom',
}

export enum ChemicalNotation {
  SMILES = 'Smiles',
  INCHI = 'InChI',
}

export const MoleculeColors = [
  'ffaaaa',
  'aaffaa',
  'aaaaff',
  'aaffff',
  'ffaaff',
  'ffffaa',
  'fffaaa',
  'aaafff',
  'aaaaaa',
  'ffffff',
];
